import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import type { SchrijvenScore, SchrijvenTaskType, SchrijvenVariantContext } from "@a2/core";
import { isSchrijvenTaskType, SCHRIJVEN_TASK_LABELS } from "@a2/core";
import { authedFetch } from "../../lib/api-client";
import { useT } from "../../lib/use-t";
import { Button } from "../../components/ui/Button";
import { SchrijvenRunnerShell } from "../../components/schrijven/SchrijvenRunnerShell";
import { SchrijvenGutter } from "../../components/schrijven/SchrijvenGutter";
import { SchrijvenWordMeter } from "../../components/schrijven/SchrijvenWordMeter";
import { SchrijvenTutorLive, useSchrijvenTips } from "../../components/schrijven/SchrijvenTutorLive";
import { SchrijvenFeedbackBubble } from "../../components/schrijven/SchrijvenFeedbackBubble";
import { EmailComposerShell } from "../../components/schrijven/EmailComposerShell";
import { WijkkrantShell } from "../../components/schrijven/WijkkrantShell";
import { FormShell } from "../../components/schrijven/FormShell";
import { BriefjeShell } from "../../components/schrijven/BriefjeShell";
import { useBulletCoverage } from "../../lib/schrijven/use-bullet-coverage";
import { useWordCount } from "../../lib/schrijven/use-word-count";
import { SCHRIJVEN_MIN_WORDS, registerToneFor } from "../../lib/schrijven/runner-constants";
import {
  clearResumableSession,
  saveResumableSession,
} from "../../lib/exam-session-resume";

type LocationState = {
  sessionId?: string;
  variantIds?: Partial<Record<SchrijvenTaskType, string>>;
};

type HydratedTurn = {
  id: string;
  taskType: SchrijvenTaskType;
  variantId: string | null;
  userText: string;
  variant: SchrijvenVariantContext | null;
};

type SessionData = {
  turns: HydratedTurn[];
};

type GradeResult = {
  score: SchrijvenScore;
  band: string;
  bandLabel: string;
  tutorParagraph: string;
  tutorParagraphTranslation: string | null;
};

export function SchrijvenPracticePage() {
  const { taskType: rawTaskType } = useParams<{ taskType: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const t = useT();
  const state = (location.state ?? {}) as LocationState;

  const taskType = rawTaskType && isSchrijvenTaskType(rawTaskType) ? rawTaskType : null;

  const [sessionId] = useState<string | null>(state.sessionId ?? null);
  const [sessionData, setSessionData] = useState<SessionData | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Save resumable session.
  useEffect(() => {
    if (sessionId) saveResumableSession("practice", sessionId);
  }, [sessionId]);

  // Load session data.
  useEffect(() => {
    if (!sessionId) return;
    authedFetch(`/schrijven/sessions/${sessionId}`)
      .then((r) => r.json())
      .then((body: { ok: boolean; data?: SessionData; error?: { message: string } }) => {
        if (!body.ok) {
          setLoadError(body.error?.message ?? t("schrijven.runner.noSession"));
          return;
        }
        setSessionData(body.data ?? null);
      })
      .catch((e: unknown) => setLoadError(e instanceof Error ? e.message : t("chrome.common.unknownError")));
  }, [sessionId]);

  if (!taskType) {
    return <ErrorState message={t("schrijven.runner.noVariant")} back={t("schrijven.runner.backToSchrijven")} />;
  }
  if (loadError) {
    return <ErrorState message={loadError} back={t("schrijven.runner.backToSchrijven")} />;
  }
  if (!sessionData && sessionId) {
    return <LoadingState message={t("schrijven.runner.loading")} />;
  }

  const turn = sessionData?.turns.find((tn) => tn.taskType === taskType);
  const variant = turn?.variant ?? null;

  if (!variant) {
    return <ErrorState message={t("schrijven.runner.noVariant")} back={t("schrijven.runner.backToSchrijven")} />;
  }

  return (
    <PracticeBody
      taskType={taskType}
      variant={variant}
      sessionId={sessionId}
      onClose={() => navigate("/schrijven")}
      onReport={() => {
        if (sessionId) navigate(`/schrijven/report/${sessionId}`);
      }}
    />
  );
}

function PracticeBody({
  taskType,
  variant,
  sessionId,
  onClose,
  onReport,
}: {
  taskType: SchrijvenTaskType;
  variant: SchrijvenVariantContext;
  sessionId: string | null;
  onClose: () => void;
  onReport: () => void;
}) {
  const t = useT();
  const tips = useSchrijvenTips();

  const [text, setText] = useState("");
  const [subject, setSubject] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<GradeResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const words = useWordCount(text);
  const covered = useBulletCoverage(taskType, text, variant.bullets);

  const tone = registerToneFor(variant.recipientRelationship);
  const registerLabel =
    tone === "formal"
      ? t("schrijven.runner.registerFormal")
      : tone === "informal"
        ? t("schrijven.runner.registerInformal")
        : null;

  async function handleSubmit() {
    if (!sessionId || !text.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await authedFetch(`/schrijven/sessions/${sessionId}/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ taskType, userText: text }),
      });
      const body = (await res.json()) as
        | { ok: true; data: GradeResult }
        | { ok: false; error: { message: string } };
      if (!body.ok) {
        setError(body.error.message);
        return;
      }
      setResult(body.data);
      clearResumableSession("practice");
    } catch (e) {
      setError(e instanceof Error ? e.message : t("chrome.common.unknownError"));
    } finally {
      setSubmitting(false);
    }
  }

  const taskLabel = SCHRIJVEN_TASK_LABELS[taskType];

  const editorSlot = result ? (
    <div className="flex-1 min-h-0 overflow-auto space-y-4">
      <SchrijvenFeedbackBubble
        score={result.score}
        tutorParagraph={result.tutorParagraph}
        tutorParagraphTranslation={result.tutorParagraphTranslation}
        bullets={variant.bullets.map((b) => ({ text: b, covered: false }))}
      />
      <div className="flex gap-3">
        <Button variant="ghost" size="md" onClick={onClose}>
          {t("schrijven.runner.backToSchrijven")}
        </Button>
        {sessionId ? (
          <Button variant="primary" size="md" onClick={onReport}>
            {t("schrijven.runner.toReport")}
          </Button>
        ) : null}
      </div>
    </div>
  ) : (
    <>
      <ComposerForType
        taskType={taskType}
        variant={variant}
        value={text}
        onChange={setText}
        disabled={submitting}
        subject={subject}
        onSubjectChange={setSubject}
      />
      {error ? (
        <p role="alert" aria-live="assertive" className="text-sm text-[var(--fail)]">
          {error}
        </p>
      ) : null}
    </>
  );

  return (
    <SchrijvenRunnerShell
      taskLabel={taskLabel}
      stepCounter={null}
      timer={null}
      onClose={onClose}
      opdracht={
        <SchrijvenGutter
          variant={variant}
          bulletCovered={result ? undefined : covered}
          minWords={SCHRIJVEN_MIN_WORDS}
          registerLabel={registerLabel}
        />
      }
      opdrachtTitle={taskLabel}
      editor={editorSlot}
      tutor={result ? null : <SchrijvenTutorLive tips={tips} />}
      wordMeter={<SchrijvenWordMeter words={words} min={SCHRIJVEN_MIN_WORDS} />}
      wordMeterCompact={<SchrijvenWordMeter words={words} min={SCHRIJVEN_MIN_WORDS} compact />}
      actions={
        result ? null : (
          <>
            <Button variant="secondary" size="md" disabled>
              {t("schrijven.runner.saveDraft")}
            </Button>
            <Button variant="warm" size="md" onClick={handleSubmit} disabled={submitting || !text.trim()}>
              {submitting ? t("schrijven.runner.submitBusy") : t("schrijven.runner.submit")}
              <SendIcon />
            </Button>
          </>
        )
      }
      actionsCompact={
        result ? null : (
          <Button variant="warm" size="md" onClick={handleSubmit} disabled={submitting || !text.trim()}>
            {submitting ? t("schrijven.runner.submitBusy") : t("schrijven.runner.submitShort")}
            <SendIcon />
          </Button>
        )
      }
    />
  );
}

function ComposerForType({
  taskType,
  variant,
  value,
  onChange,
  disabled,
  subject,
  onSubjectChange,
}: {
  taskType: SchrijvenTaskType;
  variant: SchrijvenVariantContext;
  value: string;
  onChange: (v: string) => void;
  disabled: boolean;
  subject: string;
  onSubjectChange: (v: string) => void;
}) {
  switch (taskType) {
    case "email":
      return (
        <EmailComposerShell
          variant={variant}
          value={value}
          onChange={onChange}
          disabled={disabled}
          subject={subject}
          onSubjectChange={onSubjectChange}
        />
      );
    case "wijkkrant":
      return <WijkkrantShell variant={variant} value={value} onChange={onChange} disabled={disabled} />;
    case "form":
      return <FormShell variant={variant} value={value} onChange={onChange} disabled={disabled} />;
    case "briefje":
      return <BriefjeShell variant={variant} value={value} onChange={onChange} disabled={disabled} />;
  }
}

function SendIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden>
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  );
}

function ErrorState({ message, back }: { message: string; back: string }) {
  return (
    <div className="flex flex-col items-center justify-center h-dvh gap-4 px-6 bg-[var(--bg)]">
      <p className="text-[var(--fail)] text-sm">{message}</p>
      <a href="/schrijven" className="text-sm text-[var(--ink-muted)] underline">
        {back}
      </a>
    </div>
  );
}

function LoadingState({ message }: { message: string }) {
  return (
    <div className="flex items-center justify-center h-dvh bg-[var(--bg)]">
      <p className="text-[var(--ink-faint)] text-sm">{message}</p>
    </div>
  );
}
